import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ServiceTemplateFormData } from '../types/forms';
import { getServiceTemplateChanges } from '../utils/changeDetection';

interface ServiceTemplateFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data?: ServiceTemplateFormData | Partial<ServiceTemplateFormData>) => void;
  formData: ServiceTemplateFormData;
  onInputChange: (field: keyof ServiceTemplateFormData) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  title: string;
  submitButtonText: string;
  isEdit?: boolean;
}

const ServiceTemplateFormModal: React.FC<ServiceTemplateFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  formData,
  onInputChange,
  title,
  submitButtonText,
  isEdit = false,
}) => {
  const { t } = useTranslation();
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [originalFormData, setOriginalFormData] = useState<ServiceTemplateFormData | null>(null);

  const handleFieldBlur = (fieldName: string) => {
    setTouchedFields(prev => new Set(prev).add(fieldName));
  };

  const shouldShowError = (fieldName: string, hasError: boolean) => {
    return touchedFields.has(fieldName) && hasError;
  };

  // Store original form data when editing
  useEffect(() => {
    if (open && isEdit && !originalFormData) {
      setOriginalFormData({ ...formData });
    } else if (!open) {
      setOriginalFormData(null);
    }
  }, [open, isEdit, formData, originalFormData]);

  const isFormValid = () => {
    const priceValue = parseFloat(formData.price);
    return (
      formData.name.trim() !== '' &&
      formData.estimated_duration > 0 &&
      !isNaN(priceValue) && priceValue > 0
    );
  };

  // Check if form data has changed from original
  const hasFormDataChanged = (): boolean => {
    if (!isEdit || !originalFormData) {
      return true; // For new service templates, always allow submission
    }

    return (
      formData.name !== originalFormData.name ||
      formData.description !== originalFormData.description ||
      formData.estimated_duration !== originalFormData.estimated_duration ||
      formData.price !== originalFormData.price
    );
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (isFormValid()) {
      if (isEdit && originalFormData) {
        // For edit mode, only send changed fields
        const changedData = getServiceTemplateChanges(originalFormData, formData);
        onSubmit(changedData);
      } else {
        // For create mode, send all data
        onSubmit(formData);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {title}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ ml: 1 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
             <TextField
               label={t('forms.serviceName')}
               value={formData.name}
               onChange={onInputChange('name')}
               onBlur={() => handleFieldBlur('name')}
               fullWidth
               required
               autoFocus={false}
               error={shouldShowError('name', formData.name.trim() === '')}
               helperText={shouldShowError('name', formData.name.trim() === '') ? t('forms.serviceNameRequired') : ''}
             />
            
            <TextField
              label={t('forms.serviceDescription')}
              value={formData.description || ''}
              onChange={onInputChange('description')}
              fullWidth
              multiline
              rows={3}
              placeholder={t('forms.serviceDescriptionPlaceholder')}
            />
            
            <Box display="flex" gap={2}>
               <TextField
                 label={t('forms.hours')}
                 type="number"
                 value={Math.floor(formData.estimated_duration / 60)}
                 onChange={(e) => {
                   const value = e.target.value;
                   if (value === '') {
                     const minutes = formData.estimated_duration % 60;
                     onInputChange('estimated_duration')({ target: { value: minutes.toString() } } as any);
                     return;
                   }
                   const hours = parseInt(value) || 0;
                   const minutes = formData.estimated_duration % 60;
                   const totalMinutes = hours * 60 + minutes;
                   onInputChange('estimated_duration')({ target: { value: totalMinutes.toString() } } as any);
                 }}
                 onBlur={() => handleFieldBlur('duration')}
                 fullWidth
                 required
                 inputProps={{ min: 0 }}
                 error={shouldShowError('duration', formData.estimated_duration <= 0)}
                 helperText={shouldShowError('duration', formData.estimated_duration <= 0) ? t('forms.durationGreaterThanZero') : ''}
               />
              
              <TextField
                label={t('forms.minutes')}
                type="number"
                value={formData.estimated_duration % 60}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    const hours = Math.floor(formData.estimated_duration / 60);
                    onInputChange('estimated_duration')({ target: { value: (hours * 60).toString() } } as any);
                    return;
                  }
                  const minutes = parseInt(value) || 0;
                  const hours = Math.floor(formData.estimated_duration / 60);
                  const totalMinutes = hours * 60 + minutes;
                  onInputChange('estimated_duration')({ target: { value: totalMinutes.toString() } } as any);
                }}
                fullWidth
                required
                inputProps={{ min: 0, max: 59 }}
                error={formData.estimated_duration <= 0}
              />
              
               <TextField
                 label={t('forms.servicePrice')}
                 type="number"
                 value={formData.price}
                 onChange={onInputChange('price')}
                 onBlur={() => handleFieldBlur('price')}
                 fullWidth
                 required
                 inputProps={{ min: 0, step: 1 }}
                 error={shouldShowError('price', isNaN(parseFloat(formData.price)) || parseFloat(formData.price) <= 0)}
                 helperText={shouldShowError('price', isNaN(parseFloat(formData.price)) || parseFloat(formData.price) <= 0) ? t('forms.priceValidNumber') : ''}
               />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={!isFormValid() || (isEdit && !hasFormDataChanged())}
          >
            {submitButtonText}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ServiceTemplateFormModal;
